import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import type { GitHubInstallation, GitHubInstallationDetail, GitHubRepo } from '../types';

export default function GitHubSettings() {
  const [searchParams] = useSearchParams();
  const [installations, setInstallations] = useState<GitHubInstallation[]>([]);
  const [selectedInstallation, setSelectedInstallation] = useState<GitHubInstallationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [syncing, setSyncing] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [claimId, setClaimId] = useState('');
  const [claiming, setClaiming] = useState(false);

  const fetchInstallations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getGitHubInstallations();
      setInstallations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load installations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstallations();
  }, [fetchInstallations]);

  // Handle OAuth callback success message
  useEffect(() => {
    const success = searchParams.get('success');
    const installationId = searchParams.get('installation_id');
    const errorParam = searchParams.get('error');

    if (success === 'true' && installationId) {
      setSuccessMessage('GitHub App installed successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } else if (errorParam) {
      setError(`GitHub connection failed: ${errorParam}`);
    }
  }, [searchParams]);

  const handleConnectGitHub = async () => {
    try {
      const { url } = await api.getGitHubInstallUrl();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get install URL');
    }
  };

  const handleSelectInstallation = async (installation: GitHubInstallation) => {
    setLoadingDetail(true);
    try {
      const detail = await api.getGitHubInstallation(installation.id);
      setSelectedInstallation(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load installation details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSyncInstallation = async (installationId: number) => {
    setSyncing(installationId);
    try {
      const result = await api.syncGitHubInstallation(installationId);
      setSuccessMessage(`Synced ${result.synced_count} repositories`);
      setTimeout(() => setSuccessMessage(''), 3000);
      // Refresh the installation detail
      if (selectedInstallation?.id === installationId) {
        const detail = await api.getGitHubInstallation(installationId);
        setSelectedInstallation(detail);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync installation');
    } finally {
      setSyncing(null);
    }
  };

  const handleClaimInstallation = async () => {
    const id = parseInt(claimId.trim(), 10);
    if (!id) {
      setError('Please enter a valid installation ID');
      return;
    }
    setClaiming(true);
    setError('');
    try {
      const installation = await api.claimGitHubInstallation(id);
      setInstallations([installation]);
      setClaimId('');
      setSuccessMessage('GitHub account connected successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to claim installation');
    } finally {
      setClaiming(false);
    }
  };

  const handleRemoveInstallation = async (installationId: number) => {
    // if (!confirm('Are you sure you want to remove this GitHub connection?')) {
    //   return;
    // }
    console.log("HELP>>>");
    try {
      await api.removeGitHubInstallation(installationId);
      setInstallations(installations.filter(i => i.id !== installationId));
      if (selectedInstallation?.id === installationId) {
        setSelectedInstallation(null);
      }
      setSuccessMessage('GitHub connection removed');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove installation');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Connect GitHub repositories to your managed sites for Hugo/Gatsby workflows
        </p>
        <button
          onClick={handleConnectGitHub}
          className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
        >
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
          </svg>
          Connect GitHub
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-500 hover:text-red-700 dark:hover:text-red-300">&times;</button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Installations List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Connected Accounts</h2>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading...</div>
              ) : installations.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No GitHub accounts connected yet.
                  <br />
                  <button
                    onClick={handleConnectGitHub}
                    className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Connect your first account
                  </button>
                  <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                      Already installed? Paste your installation ID from<br />
                      <span className="font-mono">github.com/settings/installations/</span><strong>{'{'}<em>id</em>{'}'}</strong>
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Installation ID"
                        value={claimId}
                        onChange={e => setClaimId(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleClaimInstallation()}
                        className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleClaimInstallation}
                        disabled={claiming || !claimId.trim()}
                        className="text-sm px-3 py-1 bg-gray-900 text-white rounded hover:bg-gray-800 disabled:opacity-50"
                      >
                        {claiming ? 'Connecting...' : 'Connect'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                installations.map((installation) => (
                  <div
                    key={installation.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      selectedInstallation?.id === installation.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                    }`}
                    onClick={() => handleSelectInstallation(installation)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                            <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{installation.account_login}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{installation.account_type}</p>
                        </div>
                      </div>
                      {installation.suspended_at && (
                        <span className="text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 px-2 py-1 rounded">
                          Suspended
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Installation Detail */}
        <div className="lg:col-span-2">
          {loadingDetail ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
              Loading installation details...
            </div>
          ) : selectedInstallation ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {selectedInstallation.account_login}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedInstallation.repos.length} repositories available
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSyncInstallation(selectedInstallation.id)}
                    disabled={syncing === selectedInstallation.id}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {syncing === selectedInstallation.id ? 'Syncing...' : 'Sync Repos'}
                  </button>
                  <button
                    onClick={() => handleRemoveInstallation(selectedInstallation.id)}
                    className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                {selectedInstallation.repos.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No repositories accessible. Make sure you granted access to repositories when installing the app.
                  </div>
                ) : (
                  selectedInstallation.repos.map((repo) => (
                    <RepoItem key={repo.id} repo={repo} />
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
              Select an account to view its repositories
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RepoItem({ repo }: { repo: GitHubRepo }) {
  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <a
              href={repo.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 truncate"
            >
              {repo.full_name}
            </a>
            {repo.private && (
              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                Private
              </span>
            )}
          </div>
          {repo.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">{repo.description}</p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Default branch: {repo.default_branch}
            {repo.last_synced_at && (
              <> &bull; Last synced: {new Date(repo.last_synced_at).toLocaleDateString()}</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
